type EventHandler = (payload?: unknown) => void;

class EventBus {
  private queue: Record<string, EventHandler[]>;
  private onceQueue: Record<string, EventHandler[]>;
  private payloads: Map<string, unknown>;

  constructor() {
    this.queue = {};
    this.onceQueue = {};
    this.payloads = new Map();
  }

  on(name: string, callback: EventHandler) {
    this.queue[name] = this.queue[name] || [];
    this.queue[name].push(callback);
  }

  once(name: string, callback: EventHandler) {
    this.onceQueue[name] = this.onceQueue[name] || [];
    this.onceQueue[name].push(callback);
  }

  off(name: string, callback?: EventHandler) {
    if (callback) {
      if (this.queue[name]) {
        for (let i = 0; i < this.queue[name].length; i += 1) {
          if (this.queue[name][i] === callback) {
            this.queue[name].splice(i, 1);
            break;
          }
        }
      }
    } else {
      delete this.queue[name];
    }
  }

  emit(name: string, data?: unknown) {
    this.payloads.set(name, data);
    if (this.queue[name]) {
      this.queue[name].forEach((callback) => {
        callback(data);
      });
    }
    if (this.onceQueue[name]) {
      this.onceQueue[name].forEach((callback) => {
        callback(data);
      });
      delete this.onceQueue[name];
    }
  }

  get(name: string) {
    return this.payloads.get(name);
  }
}

export type PageRefreshPayload = {
  scope: string;
  action?: string;
  source?: string;
  data?: unknown;
};

export const PAGE_REFRESH_EVENT = "page-refresh";

const eventBus = new EventBus();

export const emitPageRefresh = (payload: PageRefreshPayload) => {
  eventBus.emit(PAGE_REFRESH_EVENT, payload);
};

export const onPageRefresh = (
  scope: string,
  callback: (payload: PageRefreshPayload) => void
) => {
  const handler = (payload?: unknown) => {
    if (!payload || typeof payload !== "object") return;
    const nextPayload = payload as PageRefreshPayload;
    if (nextPayload.scope !== scope) return;
    callback(nextPayload);
  };

  eventBus.on(PAGE_REFRESH_EVENT, handler);
  return () => eventBus.off(PAGE_REFRESH_EVENT, handler);
};

export default eventBus;
